import React, { useCallback, useState } from "react";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import {
  Filter,
  TextInput,
  Identifier,
  Datagrid,
  TextField,
  FunctionField,
  WithPermissions,
  Button,
  useRefresh,
  ReactAdminComponentProps,
  Record,
  Responsive,
} from "react-admin";
import { AppbarGroupsFilter } from "../../common/data-grid-inputs/AppBarGroupsFilter";
import { Void } from "../../common/data-grid-fields/Void";
import resourceNames from "../../../common/data/resourceNames";
import { Theme, Typography, Tooltip } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import "./VehiclesWithUnidentifiedEventsList.css";
import { Route } from "react-router-dom";
import { listOnlyComponentProps } from "../../../common/routes/customRoutes";
import { AssignUnidentifiedDrivingForm } from "./AssignUnidentifiedDrivingForm";
import { BulkAssignUnidentifiedDrivingForm } from "./BulkAssignUnidentifiedDrivingForm";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import { GenericDateOrTimePicker } from "../../common/form/GenericDateOrTimePicker";
import { DatePicker } from "@material-ui/pickers";
import { endOfToday, addDays } from "date-fns";
import { History } from "history";
import { ProLogsEmpty } from "../../common/list/ProLogsEmpty";
import unidentifiedDriving from ".";
import { parseISO } from "date-fns/esm";
import { UnidentifiedEventsListDto } from "../../../common/data/service-proxies";
import { DefaultProLogsList } from "../../common/list/DefaultProLogsList";
import { useResetListSearchField } from "../../../common/hooks/useResetListSearchField";
import SelectSimpleList from "./SimpleSelectList";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import AssignmentIcon from "@material-ui/icons/AssignmentTurnedIn";
import { Link } from "react-router-dom";
import { CalendarTodayOutlined } from "@material-ui/icons";

interface BulkActionButtonsProps {
  onClose: () => void;
  history?: History<unknown>;
  selectedIds?: Array<Identifier>;
}

interface VehiclesWithUnidentifiedEventsListProps
  extends ReactAdminComponentProps {}

const ListFilter = (props: any) => {
  useSearchFields(props, ["vehicleName"]);
  useResetListSearchField(props);
  return (
    <Filter {...props}>
      <TextInput
        className="list-view-filter search-box"
        label="resources.common.labels.search"
        source="q"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        options={{
          autoOk: true,
        }}
        className="list-view-filter"
        label="resources.hos-unidentified-driving.labels.dateFrom"
        source="dateFrom"
        alwaysOn
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        options={{
          autoOk: true,
        }}
        className="list-view-filter"
        label="resources.hos-unidentified-driving.labels.dateTo"
        source="dateTo"
        alwaysOn
      />
    </Filter>
  );
};

const renderPositionName = (
  location: string | undefined | null,
  theme: Theme
) => {
  if (!location) {
    return "Unknown";
  }

  const lastPositionName: string = location;
  return (
    <div className="last-position-container">
      <div style={{ color: theme.palette.info.main }}>
        <LocationOnIcon />
      </div>
      <div>
        <Responsive
          medium={<>{lastPositionName}</>}
          small={<>{lastPositionName}</>}
        ></Responsive>
      </div>
    </div>
  );
};

const BulkActionButtons = (props: BulkActionButtonsProps) => {
  return (
    <>
      <Button
        label="resources.hos-unidentified-driving.labels.assign"
        onClick={() =>
          props.history?.push(
            `/${resourceNames.HOS_UNIDENTIFIED_DRIVING}/bulkAssign`
          )
        }
      >
        <AssignmentTurnedInIcon />
      </Button>
      <Route
        exact
        path={`/${resourceNames.HOS_UNIDENTIFIED_DRIVING}/bulkAssign`}
        render={(routeProps) => (
          <WithPermissions
            render={(renderProps) => (
              <BulkAssignUnidentifiedDrivingForm
                {...routeProps}
                selectedIds={props.selectedIds ?? []}
                {...renderProps}
                onClose={props.onClose}
              />
            )}
            resource={resourceNames.HOS_UNIDENTIFIED_DRIVING}
            basePath={`/${resourceNames.HOS_UNIDENTIFIED_DRIVING}`}
            {...props}
          />
        )}
      />
    </>
  );
};

export const VehiclesWithUnidentifiedEventsList = (
  props: VehiclesWithUnidentifiedEventsListProps
) => {
  const theme: Theme = useTheme();
  const [selectedGroup, setSelectedGroup] = useState<Identifier>("");
  const refresh = useRefresh();

  const handleClose = useCallback(() => {
    props.history?.push(`/${resourceNames.HOS_UNIDENTIFIED_DRIVING}`);
    refresh();
  }, [props.history, refresh]);

  interface SimpleListProps {
    record?: Record;
  }

  const RenderPrimaryTextForSimpleList = ({ record }: SimpleListProps) => {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <Tooltip title="resources.hos-unidentified-driving.labels.vehicleName">
            <div
              style={{
                display: "flex",
                gap: "6px",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LocalShippingOutlinedIcon
                style={{
                  color: theme.palette.success.main,
                }}
              />
              <TextField
                record={record}
                source="vehicleName"
                label="resources.hos-unidentified-driving.labels.vehicleName"
              />
            </div>
          </Tooltip>
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalendarTodayOutlined
              style={{
                color: theme.palette.success.main,
                width: 20,
              }}
            />
            <FunctionField<UnidentifiedEventsListDto & Record>
              source="startTime"
              record={record as UnidentifiedEventsListDto & Record}
              label="resources.hos-unidentified-driving.labels.startTime"
              render={(record) => {
                return record?.startTime ? (
                  <Typography variant="body2">
                    {parseISO(record.startTime).toLocaleString(
                      "en-US",
                      record.homeTerminalTimeZone
                        ? { timeZone: record.homeTerminalTimeZone }
                        : undefined
                    )}
                  </Typography>
                ) : null;
              }}
            />
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "1px",
            flexWrap: "wrap",
          }}
        >
          <Typography variant="body2" style={{ fontWeight: "600" }}>
            Duration
          </Typography>
          <TextField
            record={record}
            source="duration"
            label="resources.hos-unidentified-driving.labels.duration"
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "4px",
            flexWrap: "wrap",
          }}
        >
          <FunctionField<UnidentifiedEventsListDto & Record>
            record={record as UnidentifiedEventsListDto & Record}
            source="startLocation"
            render={(record) =>
              renderPositionName(record?.startLocation, theme)
            }
            label="resources.hos-unidentified-driving.labels.startLocation"
          />
          -
          <FunctionField<UnidentifiedEventsListDto & Record>
            record={record as UnidentifiedEventsListDto & Record}
            source="endLocation"
            render={(record) => renderPositionName(record?.endLocation, theme)}
            label="resources.hos-unidentified-driving.labels.endLocation"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="content-container">
      <AppbarGroupsFilter
        selectedGroup={selectedGroup}
        onSelectedGroupChange={(id) => setSelectedGroup(id)}
      />
      <DefaultProLogsList
        title="resources.hos-unidentified-driving.name"
        actions={<Void />}
        filters={<ListFilter />}
        filter={!!selectedGroup ? { groupId: selectedGroup } : {}}
        filterDefaultValues={{
          dateFrom: addDays(endOfToday(), -7).toISOString(),
          dateTo: endOfToday().toISOString(),
        }}
        bulkActionButtons={
          <BulkActionButtons
            history={props.history}
            onClose={() => refresh()}
          />
        }
        empty={<ProLogsEmpty iconType={unidentifiedDriving.icon} />}
        {...props}
      >
        <Responsive
          small={
            <SelectSimpleList
              className="simple-list-wrapper with-border"
              primaryText={(record) => (
                <RenderPrimaryTextForSimpleList record={record} />
              )}
              rightIcon={(record) => (
                <Link
                  to={`/${resourceNames.HOS_UNIDENTIFIED_DRIVING}/assign/${record.id}`}
                >
                  <AssignmentIcon
                    style={{
                      color: theme.palette.primary.main,
                    }}
                  />
                </Link>
              )}
            />
          }
          medium={
            <Datagrid
              rowClick={(id, _basePath, _record) => {
                return `/${resourceNames.HOS_UNIDENTIFIED_DRIVING}/assign/${id}`;
              }}
            >
              <TextField
                source="vehicleName"
                label="resources.hos-unidentified-driving.labels.vehicleName"
              />

              <FunctionField<UnidentifiedEventsListDto & Record>
                source="startTime"
                label="resources.hos-unidentified-driving.labels.startTime"
                render={(record) => {
                  return record?.startTime ? (
                    <Typography variant="body2">
                      {parseISO(record.startTime).toLocaleString(
                        "en-US",
                        record.homeTerminalTimeZone
                          ? { timeZone: record.homeTerminalTimeZone }
                          : undefined
                      )}
                    </Typography>
                  ) : null;
                }}
              />

              <FunctionField<UnidentifiedEventsListDto & Record>
                source="distance"
                render={(record) => `${record?.distance} mi`}
                label="resources.hos-unidentified-driving.labels.distance"
              />

              <TextField
                source="duration"
                label="resources.hos-unidentified-driving.labels.duration"
              />

              <FunctionField<UnidentifiedEventsListDto & Record>
                source="startLocation"
                render={(record) =>
                  renderPositionName(record?.startLocation, theme)
                }
                label="resources.hos-unidentified-driving.labels.startLocation"
              />
              <FunctionField<UnidentifiedEventsListDto & Record>
                source="endLocation"
                render={(record) =>
                  renderPositionName(record?.endLocation, theme)
                }
                label="resources.hos-unidentified-driving.labels.endLocation"
              />
            </Datagrid>
          }
        />
      </DefaultProLogsList>
      <Route
        exact
        path={`/${resourceNames.HOS_UNIDENTIFIED_DRIVING}/assign/:id`}
        render={(routeProps) => (
          <WithPermissions
            render={(renderProps) => (
              <AssignUnidentifiedDrivingForm
                {...renderProps}
                {...routeProps}
                onClose={handleClose}
              />
            )}
            resource={resourceNames.HOS_UNIDENTIFIED_DRIVING}
            basePath={`/${resourceNames.HOS_UNIDENTIFIED_DRIVING}`}
            {...listOnlyComponentProps}
          />
        )}
      />
    </div>
  );
};
